import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from '../api';
import ReactionButtons from './ReactionButtons';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import './Comments.css';

const ForumPostComments = ({ contentId, isFirstPost = false, onCommentAdded, onReactionChange, ownerId }) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const { isAuthenticated } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  // Initialize Tiptap editor - same config as Profile.js
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false, // Disable default Link extension to avoid duplication
      }),
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Share your thoughts...',
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setEditorContent(editor.getText().trim());
    },
  });

  // Focus editor when form is shown
  useEffect(() => {
    if (showForm && editor) {
      const timer = setTimeout(() => {
        editor.commands.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [showForm, editor]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!editor || isSubmitting) return;
    
    const htmlContent = editor.getHTML();
    const textContent = editor.getText().trim();
    
    if (!textContent) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL || '/api'}/comments/forum-post/${contentId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: htmlContent
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        editor.commands.clearContent();
        setEditorContent('');
        setShowForm(false);
        
        if (onCommentAdded) {
          onCommentAdded(data.comment);
        }
      } else {
        console.error('Failed to submit comment');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const MenuBar = () => {
    if (!editor) {
      return null;
    }

    return (
      <div style={{
        border: '1px solid #ddd',
        borderBottom: 'none',
        borderRadius: '4px 4px 0 0',
        backgroundColor: '#f8f9fa',
        padding: '0.5rem',
        display: 'flex',
        gap: '0.25rem',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('bold') ? '#38b44a' : 'white',
            color: editor.isActive('bold') ? 'white' : '#333',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.875rem'
          }}
        >
          B
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('italic') ? '#38b44a' : 'white',
            color: editor.isActive('italic') ? 'white' : '#333',
            cursor: 'pointer',
            fontStyle: 'italic',
            fontSize: '0.875rem'
          }}
        >
          I
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('strike') ? '#38b44a' : 'white',
            color: editor.isActive('strike') ? 'white' : '#333',
            cursor: 'pointer',
            textDecoration: 'line-through',
            fontSize: '0.875rem'
          }}
        >
          S
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('heading', { level: 2 }) ? '#38b44a' : 'white',
            color: editor.isActive('heading', { level: 2 }) ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('bulletList') ? '#38b44a' : 'white',
            color: editor.isActive('bulletList') ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('orderedList') ? '#38b44a' : 'white',
            color: editor.isActive('orderedList') ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          1. List
        </button>
      </div>
    );
  };

  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <div className="comments-section forum-post">
      <div className="comment-header">
        {/* Show Add Comment button or Reply link */}
        <div 
          className="comment-actions"
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginTop: '0',
            width: '100%'
          }}
        >
          <ReactionButtons 
            contentType="forum-post" 
            contentId={contentId}
            style={{ fontSize: '0.75rem' }}
            onReactionChange={onReactionChange}
            ownerId={ownerId}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowForm(!showForm);
            }}
            className={isFirstPost ? "add-comment-button" : "reply-button"}
            style={!isFirstPost ? {
              background: 'none',
              border: 'none',
              color: '#38b44a',
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '0 !important',
              borderRadius: '3px',
              textDecoration: 'underline'
            } : {}}
          >
            {showForm ? 'Cancel' : (isFirstPost ? 'Add Comment' : 'Reply')}
          </button>
        </div>
      </div>

      {showForm && editor && (
        <div className="comment-form">
          <form onSubmit={handleSubmitComment}>
            <MenuBar />
            <div style={{ 
              border: '1px solid #ddd',
              borderRadius: '0 0 4px 4px',
              backgroundColor: 'white',
              minHeight: '100px',
              padding: '0.75rem',
              marginBottom: '0.75rem'
            }}>
              <EditorContent editor={editor} />
            </div>
            <div className="comment-form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowForm(false);
                  editor.commands.clearContent();
                  setEditorContent('');
                }}
                className="cancel-button"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-button"
                disabled={!editorContent || isSubmitting}
              >
                {isSubmitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ForumPostComments;
